import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { consultasService } from '../services/api';
import { authService } from '../services/authService';
import { calendarService } from '../services/api/calendarService';
import { formatarData, formatarMoeda } from '../utils/formatters';
import { API_CONFIG } from '../services/staticDataConfig';
import MobileDataCard from '../components/MobileDataCard';
import FiltroPsicologo from '../components/FiltroPsicologo';

// CORREÇÃO: Importar tipos apenas do arquivo central
import type { Consulta, ConsultaComModal, FiltroConsultas } from '../types/consulta';

export default function ListaConsultas() {
  const [consultas, setConsultas] = useState<ConsultaComModal[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtro, setFiltro] = useState('todos');
  const [salvando, setSalvando] = useState<string | null>(null);
  
  // ✅ MULTI-PSICÓLOGOS: Filtro por psicólogo
  const [filtroPsicologo, setFiltroPsicologo] = useState<number | null>(null);
  
  // 🔍 NOVO: Filtro de busca por nome do paciente
  const [buscaPaciente, setBuscaPaciente] = useState('');

  const carregarConsultas = async () => {
    try {
      setCarregando(true);
      setErro(null);
      
      // ✅ MULTI-PSICÓLOGOS: Construir filtros com psicólogo
      const filtros: FiltroConsultas = {
        incluirCanceladas: true,
        somenteFuturas: false,
        psicologoId: filtroPsicologo || undefined
      };
      
      console.log('[ListaConsultas] Carregando consultas:', {
        incluirCanceladas: true,
        somenteFuturas: false,
        filtroPsicologo
      });
      
      const data = await consultasService.listarTodas(filtros);
      
      if (!Array.isArray(data)) {
        console.error('Erro: consultasService.listarTodas() não retornou array', data);
        setConsultas([]);
        return;
      }

      setConsultas(data.map(consulta => ({
        ...consulta,
        mostrarModalPagamento: false,
        dataPagamentoTemp: consulta.dataPagamento ? formatarData(consulta.dataPagamento) : formatarData(new Date())
      })));
    } catch (error) {
      console.error('[ERROR] Erro ao carregar consultas:', error);
      setErro('Não foi possível carregar as consultas. Tente novamente.');
      setConsultas([]);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    // Definir filtro padrão para psicólogo logado
    const usuarioAtual = authService.getUsuario();
    if (usuarioAtual?.cargo === 'psicologo') {
      const idNum = typeof usuarioAtual.id === 'string' ? parseInt(usuarioAtual.id) : usuarioAtual.id;
      if (idNum && filtroPsicologo !== idNum) {
        setFiltroPsicologo(idNum);
        // carregarConsultas será chamado pelo outro effect quando o filtro mudar
        return;
      }
    }
    carregarConsultas();
  }, []);

  // Recarregar quando o filtro por psicólogo mudar (ex.: admin seleciona)
  useEffect(() => {
    carregarConsultas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroPsicologo]);

  const abrirModalPagamento = (consultaId: string) => {
    setConsultas(prev => prev.map(consulta => 
      consulta.id === consultaId 
        ? { 
            ...consulta, 
            mostrarModalPagamento: true,
            dataPagamentoTemp: consulta.dataPagamento 
              ? formatarData(consulta.dataPagamento) 
              : formatarData(new Date().toISOString().split('T')[0])
          }
        : consulta
    ));
  };

  const fecharModalPagamento = (consultaId: string) => {
    setConsultas(prev => prev.map(consulta => 
      consulta.id === consultaId 
        ? { ...consulta, mostrarModalPagamento: false }
        : consulta
    ));
  };

  const alterarDataPagamento = (consultaId: string, novaData: string) => {
    setConsultas(prev => prev.map(consulta => 
      consulta.id === consultaId 
        ? { ...consulta, dataPagamentoTemp: novaData }
        : consulta
    ));
  };

  const confirmarPagamento = async (consultaId: string) => {
    const consulta = consultas.find(c => c.id === consultaId);
    if (!consulta || !consulta.dataPagamentoTemp) return;

    // Validar formato da data
    if (!/\d{2}\/\d{2}\/\d{4}/.test(consulta.dataPagamentoTemp)) {
      alert('Por favor, digite uma data válida no formato DD/MM/AAAA');
      return;
    }

    try {
      setSalvando(consultaId);
      
      // Converter data para formato ISO antes de enviar
      const [dia, mes, ano] = consulta.dataPagamentoTemp.split('/');
      const dataISO = `${ano}-${mes}-${dia}T00:00:00Z`;
      
      const dadosAtualizacao = {
        statusPagamento: 'pago' as const,
        dataPagamento: dataISO
      };

      await consultasService.atualizar(consultaId, dadosAtualizacao);
      
      // Atualizar estado local
      setConsultas(prev => prev.map(c => 
        c.id === consultaId 
          ? { 
              ...c, 
              statusPagamento: 'pago',
              pagamentoRecebido: true,
              dataPagamento: dataISO,
              mostrarModalPagamento: false
            }
          : c
      ));
      
    } catch (error) {
      console.error('Erro ao confirmar pagamento:', error);
      alert('Erro ao confirmar pagamento. Tente novamente.');
    } finally {
      setSalvando(null);
    }
  };

  const desmarcarPagamento = async (consultaId: string) => {
    if (!window.confirm('Tem certeza que deseja desmarcar este pagamento?')) return;
    
    try {
      setSalvando(consultaId);
      
      const dadosAtualizacao: Partial<Consulta> = {
        statusPagamento: 'pendente' as const,
        dataPagamento: undefined // Use undefined instead of null
      };

      await consultasService.atualizar(consultaId, dadosAtualizacao);
      
      // Atualizar estado local
      setConsultas(prev => prev.map(c => 
        c.id === consultaId 
          ? { 
              ...c, 
              statusPagamento: 'pendente',
              pagamentoRecebido: false,
              dataPagamento: undefined // Also use undefined here
            }
          : c
      ));
      
    } catch (error) {
      console.error('Erro ao desmarcar pagamento:', error);
      alert('Erro ao desmarcar pagamento. Tente novamente.');
    } finally {
      setSalvando(null);
    }
  };

  const cancelarConsulta = async (consultaId: string, nomePaciente: string) => {
    if (!window.confirm(`Tem certeza que deseja cancelar a consulta do paciente ${nomePaciente}?`)) return;
    
    try {
      setSalvando(consultaId);
      await consultasService.cancelar(consultaId, 'Cancelada pelo usuário');
      
      // Atualizar estado local para refletir o cancelamento
      setConsultas(prev => prev.map(c => 
        c.id === consultaId 
          ? { 
              ...c, 
              statusConsulta: 'cancelada',
              statusPagamento: 'cancelado'
            }
          : c
      ));
      
      alert('Consulta cancelada com sucesso!');
    } catch (error) {
      console.error('Erro ao cancelar consulta:', error);
      alert('Erro ao cancelar consulta. Tente novamente.');
    } finally {
      setSalvando(null);
    }
  };

  const enviarLembrete = async (consultaId: string, nomePaciente: string) => {
    if (!window.confirm(`Enviar lembrete por email para ${nomePaciente}?`)) return;
    
    try {
      setSalvando(consultaId);

      // Usar configuração centralizada de API
      const baseURL = API_CONFIG.BASE_URL;
      const response = await fetch(`${baseURL}/consultas/${consultaId}/lembrete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const resultado = await response.json();

      if (resultado.sucesso) {
        alert('✅ Lembrete enviado com sucesso!');

      } else {
        alert(`❌ Erro ao enviar lembrete: ${resultado.mensagem}`);
        console.error('[Lembrete] Falha no envio:', resultado);
      }
      
    } catch (error) {
      console.error('[Lembrete] Erro capturado:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert(`❌ Erro ao enviar lembrete: ${errorMessage}`);
    } finally {
      setSalvando(null);
    }
  };

  const criarEventoCalendar = async (consultaId: string, nomePaciente: string) => {
    if (!window.confirm(`Criar evento no Google Calendar para consulta de ${nomePaciente}?`)) return;
    
    try {
      setSalvando(consultaId);

      // Buscar dados da consulta
      const consulta = await consultasService.obterPorId(consultaId);
      if (!consulta) {
        throw new Error('Consulta não encontrada');
      }
      
      // Buscar calendários e usar o primário
      const calendarios = await calendarService.listarCalendarios();
      if (!calendarios.sucesso || !calendarios.dados || calendarios.dados.length === 0) {
        throw new Error('Nenhum calendário encontrado. Verifique a conexão com o Google Calendar.');
      }
      
      const calendarioPrimario = calendarios.dados.find((cal: any) => cal.primary) || calendarios.dados[0];
      
      // Preparar dados da consulta para o backend
      const dataConsulta = consulta.dataConsulta || consulta.data;
      const horaConsulta = consulta.horaInicio || consulta.hora;
      
      // CORREÇÃO: Garantir formato correto da data/hora ISO
      let dataHoraISO: string;
      if (horaConsulta.includes(':')) {
        // Se já tem formato HH:MM, adicionar apenas segundos se necessário
        const horaParts = horaConsulta.split(':');
        if (horaParts.length === 2) {
          dataHoraISO = `${dataConsulta}T${horaConsulta}:00`;
        } else {
          dataHoraISO = `${dataConsulta}T${horaConsulta}`;
        }
      } else {
        // Se não tem formato correto, assumir formato padrão
        dataHoraISO = `${dataConsulta}T${horaConsulta}:00:00`;
      }
      
      // CORREÇÃO: Garantir tipo correto do valor
      const valorNumerico = typeof consulta.valor === 'string' ? parseFloat(consulta.valor) : consulta.valor;
      
      const eventoConsulta = {
        pacienteNome: nomePaciente,
        dataHora: dataHoraISO,
        duracao: 60, // 1 hora padrão
        observacoes: `Evento criado para consulta ID: ${consultaId}`,
        valor: valorNumerico, // CORREÇÃO: Usar valor já convertido
        consultaId: consultaId
      };

      // Usar endpoint específico para consultas

      const response = await fetch(`${API_CONFIG.BASE_URL}/calendar/consulta`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({
          calendarId: calendarioPrimario.id,
          consulta: eventoConsulta
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const resultado = await response.json();
      
      if (resultado.sucesso && resultado.dados?.id) {
        alert(`✅ Evento criado com sucesso no Google Calendar!\n\nID do evento: ${resultado.dados.id}\n\nO paciente receberá um convite por email.`);

      } else {
        alert(`❌ Erro ao criar evento: ${resultado.mensagem}`);
        console.error('[GoogleCalendar] Falha na criação:', resultado);
      }
      
    } catch (error) {
      console.error('[GoogleCalendar] Erro capturado:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert(`❌ Erro ao criar evento no Google Calendar: ${errorMessage}`);
    } finally {
      setSalvando(null);
    }
  };

  
  // const renovarConsulta = (consulta: Consulta) => {
  //   // Navegar para nova consulta com dados preenchidos
  //   const params = new URLSearchParams({
  //     pacienteId: consulta.pacienteId,
  //     hora: consulta.horaInicio || consulta.hora,
  //     valor: consulta.valor.toString()
  //   });
  //   
  //   window.location.href = `/consultas/nova?${params.toString()}`;
  // };

  const consultasFiltradas = consultas.filter(consulta => {
    // ✅ MULTI-PSICÓLOGOS: Filtrar por psicólogo responsável (normalizando tipos)
    if (filtroPsicologo && String((consulta as any).usuarioId) !== String(filtroPsicologo)) {
      return false;
    }

    // 🔍 NOVO: Filtrar por nome do paciente
    if (buscaPaciente) {
      const termoBusca = buscaPaciente.toLowerCase();
      const nomePaciente = (consulta.nomePaciente || '').toLowerCase();
      if (!nomePaciente.includes(termoBusca)) {
        return false;
      }
    }

    switch (filtro) {
      case 'agendadas':
        return !consulta.realizada && consulta.statusConsulta === 'agendada';
      case 'realizadas':
        return consulta.realizada || consulta.statusConsulta === 'realizada';
      case 'pagas':
        return consulta.pagamentoRecebido || consulta.statusPagamento === 'pago';
      case 'pendentes':
        return (consulta.realizada || consulta.statusConsulta === 'realizada') && 
               (!consulta.pagamentoRecebido && consulta.statusPagamento !== 'pago');
      case 'canceladas':
        return consulta.statusConsulta === 'cancelada';
      case 'todos':
      default:
        return true; // Incluir TODAS as consultas, incluindo canceladas
    }
  });

  if (carregando) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border"></div>
        <p>Carregando consultas...</p>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="alert alert-danger">
        <h4 className="alert-heading">Erro ao carregar dados</h4>
        <p>{erro}</p>
        <button className="btn btn-primary" onClick={carregarConsultas}>
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header com estatísticas e ações */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="small-box bg-info">
            <div className="inner">
              <h3>{consultasFiltradas.length}</h3>
              <p>Total de Consultas</p>
            </div>
            <div className="icon">
              <i className="fas fa-calendar-check"></i>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="small-box bg-success">
            <div className="inner">
              <h3>{consultasFiltradas.filter(c => c.statusConsulta === 'realizada').length}</h3>
              <p>Realizadas</p>
            </div>
            <div className="icon">
              <i className="fas fa-check-circle"></i>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="small-box bg-warning">
            <div className="inner">
              <h3>{consultasFiltradas.filter(c => c.statusConsulta === 'agendada').length}</h3>
              <p>Agendadas</p>
            </div>
            <div className="icon">
              <i className="fas fa-clock"></i>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="small-box bg-danger">
            <div className="inner">
              <h3>{consultasFiltradas.filter(c => c.statusPagamento === 'pendente' && c.statusConsulta === 'realizada').length}</h3>
              <p>Pagamentos Pendentes</p>
            </div>
            <div className="icon">
              <i className="fas fa-money-bill-wave"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Card principal com filtros aprimorados */}
      <div className="card">
        <div className="card-header">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h3 className="card-title">
                <i className="fas fa-calendar-check mr-2"></i>
                Gerenciar Consultas
              </h3>
            </div>
            <div className="col-md-6 text-right">
              <Link to="/consultas/nova" className="btn btn-primary">
                <i className="fas fa-plus mr-2"></i>
                Nova Consulta
              </Link>
            </div>
          </div>
        </div>

        <div className="card-body">
          {/* Filtros modernizados */}
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card card-outline card-secondary">
                <div className="card-header">
                  <h5 className="card-title">
                    <i className="fas fa-filter mr-2"></i>
                    Filtros
                  </h5>
                  <div className="card-tools">
                    <button type="button" className="btn btn-tool" data-card-widget="collapse">
                      <i className="fas fa-minus"></i>
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="btn-toolbar justify-content-center consultas-filter" role="toolbar">
                    <div className="btn-group btn-group-toggle flex-wrap w-100" data-toggle="buttons">
                      <label className={`btn btn-outline-secondary ${filtro === 'todos' ? 'active' : ''}`}>
                        <input 
                          type="radio" 
                          name="filtro" 
                          value="todos"
                          checked={filtro === 'todos'} 
                          onChange={() => setFiltro('todos')} 
                        />
                        <i className="fas fa-list mr-1"></i>
                        Todas ({consultas.length})
                      </label>
                      
                      <label className={`btn btn-outline-info ${filtro === 'agendadas' ? 'active' : ''}`}>
                        <input 
                          type="radio" 
                          name="filtro" 
                          value="agendadas"
                          checked={filtro === 'agendadas'} 
                          onChange={() => setFiltro('agendadas')} 
                        />
                        <i className="fas fa-clock mr-1"></i>
                        Agendadas ({consultas.filter(c => c.statusConsulta === 'agendada').length})
                      </label>
                      
                      <label className={`btn btn-outline-success ${filtro === 'realizadas' ? 'active' : ''}`}>
                        <input 
                          type="radio" 
                          name="filtro" 
                          value="realizadas"
                          checked={filtro === 'realizadas'} 
                          onChange={() => setFiltro('realizadas')} 
                        />
                        <i className="fas fa-check-circle mr-1"></i>
                        Realizadas ({consultas.filter(c => c.statusConsulta === 'realizada').length})
                      </label>
                      
                      <label className={`btn btn-outline-primary ${filtro === 'pagas' ? 'active' : ''}`}>
                        <input 
                          type="radio" 
                          name="filtro" 
                          value="pagas"
                          checked={filtro === 'pagas'} 
                          onChange={() => setFiltro('pagas')} 
                        />
                        <i className="fas fa-dollar-sign mr-1"></i>
                        Pagas ({consultas.filter(c => c.statusPagamento === 'pago').length})
                      </label>
                      
                      <label className={`btn btn-outline-warning ${filtro === 'pendentes' ? 'active' : ''}`}>
                        <input 
                          type="radio" 
                          name="filtro" 
                          value="pendentes"
                          checked={filtro === 'pendentes'} 
                          onChange={() => setFiltro('pendentes')} 
                        />
                        <i className="fas fa-exclamation-triangle mr-1"></i>
                        Pendentes ({consultas.filter(c => c.statusConsulta === 'realizada' && c.statusPagamento === 'pendente').length})
                      </label>
                      
                      <label className={`btn btn-outline-danger ${filtro === 'canceladas' ? 'active' : ''}`}>
                        <input 
                          type="radio" 
                          name="filtro" 
                          value="canceladas"
                          checked={filtro === 'canceladas'} 
                          onChange={() => setFiltro('canceladas')} 
                        />
                        <i className="fas fa-ban mr-1"></i>
                        Canceladas ({consultas.filter(c => c.statusConsulta === 'cancelada').length})
                      </label>
                    </div>
                  </div>
                  
                  {/* 🔍 Filtros Adicionais */}
                  <div className="row mt-3">
                    {/* Busca por Nome do Paciente */}
                    <div className="col-md-4">
                      <div className="form-group mb-0">
                        <label className="form-label" htmlFor="listaConsultasBuscaPaciente">
                          <i className="fas fa-search mr-1"></i>
                          Buscar Paciente
                        </label>
                        <div className="input-group">
                          <input
                            id="listaConsultasBuscaPaciente"
                            type="text"
                            className="form-control"
                            placeholder="Digite o nome do paciente..."
                            value={buscaPaciente}
                            onChange={(e) => setBuscaPaciente(e.target.value)}
                          />
                          {buscaPaciente && (
                            <button 
                              className="btn btn-outline-secondary" 
                              type="button"
                              onClick={() => setBuscaPaciente('')}
                              title="Limpar busca"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ✅ MULTI-PSICÓLOGOS: Filtro por Psicólogo */}
                    <div className="col-md-4 offset-md-4">
                      <FiltroPsicologo
                        value={filtroPsicologo}
                        onChange={setFiltroPsicologo}
                        incluirTodos={true}
                        label="Filtrar por Psicólogo"
                        // Quando o usuário é psicólogo, o filtro fica travado no próprio id
                        disabled={authService.getUsuario()?.cargo === 'psicologo'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de consultas modernizada */}
          {consultasFiltradas.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <i className="fas fa-calendar-times fa-4x text-muted"></i>
              </div>
              <h4 className="text-muted">Nenhuma consulta encontrada</h4>
              <p className="text-muted mb-4">
                {filtro === 'todos' 
                  ? 'Não há consultas cadastradas ainda.' 
                  : `Não há consultas com o filtro "${filtro}" selecionado.`
                }
              </p>
              <Link to="/consultas/nova" className="btn btn-primary btn-lg">
                <i className="fas fa-plus mr-2"></i>
                Agendar Primeira Consulta
              </Link>
            </div>
          ) : (
            <>
            {/* Lista em cards para mobile */}
            <div className="d-block d-md-none">
              {consultasFiltradas.map(consulta => (
                <MobileDataCard
                  key={consulta.id}
                  title={consulta.nomePaciente || 'Paciente'}
                  items={[
                    { label: 'Data', value: formatarData(consulta.data) },
                    { label: 'Horário', value: (
                      <>
                        {consulta.hora}
                        {consulta.horaFim ? ` - ${consulta.horaFim}` : ''}
                      </>
                    ) },
                    { label: 'Valor', value: <span className="text-success">{formatarMoeda(consulta.valor)}</span> },
                    { label: 'Status', value: (
                      consulta.statusConsulta === 'agendada' ? <span className="badge badge-warning">Agendada</span> :
                      consulta.statusConsulta === 'realizada' ? <span className="badge badge-success">Realizada</span> :
                      consulta.statusConsulta === 'cancelada' ? <span className="badge badge-danger">Cancelada</span> :
                      <span className="badge badge-secondary">{consulta.statusConsulta}</span>
                    ) },
                    { label: 'Pagamento', value: (
                      consulta.statusPagamento === 'pago' ? <span className="badge badge-success">Pago</span> :
                      consulta.statusPagamento === 'pendente' ? <span className="badge badge-warning">Pendente</span> :
                      <span className="badge badge-secondary">{consulta.statusPagamento}</span>
                    ) }
                  ]}
                  footer={
                    <div className="btn-group btn-group-sm w-100">
                      <Link 
                        to={`/consultas/${consulta.id}/editar`}
                        className="btn btn-outline-primary"
                        title="Editar"
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      {authService.getUsuario()?.cargo !== 'atendente' && (
                        <Link 
                          to={`/consultas/${consulta.id}/prontuario`}
                          className="btn btn-outline-info"
                          title="Prontuário"
                        >
                          <i className="fas fa-file-medical"></i>
                        </Link>
                      )}
                      {consulta.statusConsulta === 'agendada' && (
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => enviarLembrete(consulta.id, consulta.nomePaciente || 'Paciente')}
                          title="Lembrete"
                          disabled={salvando === consulta.id}
                        >
                          {salvando === consulta.id ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="fas fa-envelope"></i>
                          )}
                        </button>
                      )}
                      <button
                        className="btn btn-outline-success"
                        onClick={() => criarEventoCalendar(consulta.id, consulta.nomePaciente || 'Paciente')}
                        title="Calendar"
                        disabled={salvando === consulta.id}
                      >
                        {salvando === consulta.id ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="fas fa-calendar-plus"></i>
                        )}
                      </button>
                      {consulta.statusConsulta !== 'cancelada' && (
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => cancelarConsulta(consulta.id, consulta.nomePaciente || 'Paciente')}
                          title="Cancelar"
                          disabled={salvando === consulta.id}
                        >
                          {salvando === consulta.id ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="fas fa-ban"></i>
                          )}
                        </button>
                      )}
                    </div>
                  }
                />
              ))}
            </div>

            {/* Tabela para desktop/tablet */}
            <div className="table-responsive d-none d-md-block">
              <table className="table table-hover table-striped">
                <thead className="thead-dark">
                  <tr>
                    <th>Paciente</th>
                    <th>Data</th>
                    <th>Horário</th>
                    <th>Valor</th>
                    <th>Status Consulta</th>
                    <th>Status Pagamento</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {consultasFiltradas.map(consulta => (
                    <tr key={consulta.id} className={consulta.statusConsulta === 'cancelada' ? 'table-secondary' : ''}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="user-panel d-flex">
                            <div className="image">
                              <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" 
                                   style={{ width: '35px', height: '35px', fontSize: '14px', color: 'white' }}>
                                {consulta.nomePaciente?.charAt(0).toUpperCase() || 'P'}
                              </div>
                            </div>
                            <div className="info ml-2">
                              <span className="font-weight-bold">{consulta.nomePaciente || 'Paciente não encontrado'}</span>
                              {consulta.statusConsulta === 'agendada' && (
                                <div className="mt-1">
                                  {consulta.confirmadaPaciente ? (
                                    <span className="badge badge-success" title={`Confirmado em ${consulta.dataConfirmacao ? new Date(consulta.dataConfirmacao).toLocaleDateString('pt-BR') : ''}`}>
                                      <i className="fas fa-check-circle mr-1"></i>
                                      Presença confirmada
                                    </span>
                                  ) : (
                                    <span className="badge badge-secondary">
                                      <i className="fas fa-clock mr-1"></i>
                                      Aguardando confirmação
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-light p-2">
                          <i className="fas fa-calendar mr-1"></i>
                          {formatarData(consulta.data)}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-info p-2">
                          <i className="fas fa-clock mr-1"></i>
                          {consulta.hora}
                          {consulta.horaFim && ` - ${consulta.horaFim}`}
                        </span>
                      </td>
                      <td>
                        <span className="font-weight-bold text-success">
                          {formatarMoeda(consulta.valor)}
                        </span>
                      </td>
                      <td>
                        {consulta.statusConsulta === 'agendada' && (
                          <span className="badge badge-warning p-2">
                            <i className="fas fa-clock mr-1"></i>
                            Agendada
                          </span>
                        )}
                        {consulta.statusConsulta === 'realizada' && (
                          <span className="badge badge-success p-2">
                            <i className="fas fa-check-circle mr-1"></i>
                            Realizada
                          </span>
                        )}
                        {consulta.statusConsulta === 'cancelada' && (
                          <span className="badge badge-danger p-2">
                            <i className="fas fa-ban mr-1"></i>
                            Cancelada
                          </span>
                        )}
                        {consulta.statusConsulta === 'faltou' && (
                          <span className="badge badge-secondary p-2">
                            <i className="fas fa-user-slash mr-1"></i>
                            Faltou
                          </span>
                        )}
                      </td>
                      <td>
                        {consulta.statusPagamento === 'pago' && (
                          <div>
                            <span className="badge badge-success p-2 mb-1">
                              <i className="fas fa-check-circle mr-1"></i>
                              Pago
                            </span>
                            {consulta.dataPagamento && (
                              <><br /><small className="text-muted">
                                <i className="fas fa-calendar mr-1"></i>
                                {formatarData(consulta.dataPagamento)}
                              </small></>
                            )}
                          </div>
                        )}
                        {consulta.statusPagamento === 'pendente' && (
                          <span className="badge badge-warning p-2">
                            <i className="fas fa-clock mr-1"></i>
                            Pendente
                          </span>
                        )}
                        {consulta.statusPagamento === 'cancelado' && (
                          <span className="badge badge-secondary p-2">
                            <i className="fas fa-ban mr-1"></i>
                            Cancelado
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group" role="group">
                          <Link 
                            to={`/consultas/${consulta.id}/editar`}
                            className="btn btn-sm btn-outline-primary"
                            title="Editar consulta"
                          >
                            <i className="fas fa-edit"></i>
                          </Link>
                          
                          {authService.getUsuario()?.cargo !== 'atendente' && (
                            <Link 
                              to={`/consultas/${consulta.id}/prontuario`}
                              className="btn btn-sm btn-outline-info"
                              title="Ver prontuário"
                            >
                              <i className="fas fa-file-medical"></i>
                            </Link>
                          )}

                          {/* Botão para enviar lembrete por email */}
                          {consulta.statusConsulta === 'agendada' && (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => enviarLembrete(consulta.id, consulta.nomePaciente || 'Paciente')}
                              title="Enviar lembrete por email"
                              disabled={salvando === consulta.id}
                            >
                              {salvando === consulta.id ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <i className="fas fa-envelope"></i>
                              )}
                            </button>
                          )}
                          
                          {consulta.statusConsulta === 'realizada' && consulta.statusPagamento === 'pendente' && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => abrirModalPagamento(consulta.id)}
                              title="Registrar pagamento"
                            >
                              <i className="fas fa-dollar-sign"></i>
                            </button>
                          )}
                          
                          {consulta.statusPagamento === 'pago' && (
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => desmarcarPagamento(consulta.id)}
                              title="Desmarcar pagamento"
                              disabled={salvando === consulta.id}
                            >
                              {salvando === consulta.id ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <i className="fas fa-undo"></i>
                              )}
                            </button>
                          )}
                          
                          {/* Botão para criar evento no Google Calendar */}
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => criarEventoCalendar(consulta.id, consulta.nomePaciente || 'Paciente')}
                            title="Criar evento no Google Calendar"
                            disabled={salvando === consulta.id}
                          >
                            {salvando === consulta.id ? (
                              <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                              <i className="fas fa-calendar-plus"></i>
                            )}
                          </button>

                          {consulta.statusConsulta !== 'cancelada' && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => cancelarConsulta(consulta.id, consulta.nomePaciente || 'Paciente')}
                              title="Cancelar consulta"
                              disabled={salvando === consulta.id}
                            >
                              {salvando === consulta.id ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <i className="fas fa-ban"></i>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de pagamento modernizado */}
      {consultas.map(consulta => 
        consulta.mostrarModalPagamento ? (
          <div key={consulta.id} className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header bg-success">
                  <h4 className="modal-title text-white">
                    <i className="fas fa-dollar-sign mr-2"></i>
                    Registrar Pagamento
                  </h4>
                  <button 
                    type="button" 
                    className="close text-white" 
                    onClick={() => fecharModalPagamento(consulta.id)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <h6><i className="fas fa-user mr-1"></i>{' '}Paciente:</h6>
                      <p className="font-weight-bold">{consulta.nomePaciente}</p>
                    </div>
                    <div className="col-md-6">
                      <h6><i className="fas fa-calendar mr-1"></i>{' '}Data da Consulta:</h6>
                      <p className="font-weight-bold">{formatarData(consulta.data)} às {consulta.hora}</p>
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <h6><i className="fas fa-money-bill mr-1"></i>{' '}Valor:</h6>
                      <p className="font-weight-bold text-success h5">{formatarMoeda(consulta.valor)}</p>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor={`dataPagamento-${consulta.id}`} className="form-label">
                        <i className="fas fa-calendar-check mr-2"></i>
                        Data do Pagamento:
                      </label>
                      <input
                        type="date"
                        id={`dataPagamento-${consulta.id}`}
                        className="form-control"
                        value={consulta.dataPagamentoTemp || ''}
                        onChange={(e) => alterarDataPagamento(consulta.id, e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle mr-2"></i>
                    Ao confirmar, o pagamento será registrado como recebido na data especificada.
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => fecharModalPagamento(consulta.id)}
                    disabled={salvando === consulta.id}
                  >
                    <i className="fas fa-times mr-2"></i>
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-success" 
                    onClick={() => confirmarPagamento(consulta.id)}
                    disabled={salvando === consulta.id || !consulta.dataPagamentoTemp}
                  >
                    {salvando === consulta.id ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                        Registrando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check mr-2"></i>
                        Confirmar Pagamento
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null
      )}
    </div>
  );
}
